import cn from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { useMultiFlightMap } from '../../MultiFlightMap.context';

import './legend.scss';

type LegendItem = {
  circleModifier?: 'screen';
  hasCircle?: boolean;
  icon?: string;
  isSwatch?: boolean;
  label: string;
  swatchModifier?: 'smart';
};

export function Legend() {
  const { formatMessage } = useIntl();
  const { type, user, userRoles } = useMultiFlightMap();
  const [isExpanded, setIsExpanded] = useState(true);
  const isDooh = type === CampaignFamilyEnum.dooh;
  const hasSmartLocationsPermission = checkUserResourcePermissions('smartGroups', user, userRoles);

  const items: LegendItem[] = [
    isDooh
      ? {
          circleModifier: 'screen',
          hasCircle: true,
          icon: 'fa-kit fa-screen multiFlightViewMap__screen',
          label: formatMessage({ defaultMessage: 'Screens', id: 'legend.screens' })
        }
      : {
          isSwatch: true,
          label: formatMessage({ defaultMessage: 'Activation Area', id: 'legend.activationArea' })
        },
    ...(!isDooh && hasSmartLocationsPermission
      ? [
          {
            isSwatch: true,
            label: formatMessage({ defaultMessage: 'Smart Locations', id: 'legend.smartLocations' }),
            swatchModifier: 'smart' as const
          }
        ]
      : []),
    {
      hasCircle: true,
      icon: 'fa-solid fa-thumbtack multiFlightViewMap__venueMarker--proximity',
      label: formatMessage({ defaultMessage: 'Proximity Coordinate', id: 'legend.proximityCoordinate' })
    },
    {
      icon: 'fa-solid fa-location-dot multiFlightViewMap__venueMarker--enabled',
      label: formatMessage({ defaultMessage: 'POI (Enabled attribution)', id: 'legend.poiEnabled' })
    },
    {
      icon: 'fa-solid fa-location-dot multiFlightViewMap__venueMarker--disabled',
      label: formatMessage({ defaultMessage: 'POI (Disabled attribution)', id: 'legend.poiDisabled' })
    },
    {
      icon: 'fa-solid fa-circles-overlap legend__icon--group',
      label: formatMessage({ defaultMessage: 'Item in a group', id: 'legend.itemInGroup' })
    },
    {
      icon: 'fa-solid fa-circles-overlap-3 legend__icon--group',
      label: formatMessage({ defaultMessage: 'Item in multiple groups', id: 'legend.itemInMultipleGroups' })
    }
  ];

  return (
    <div className={cn('legend', { 'legend--collapsed': !isExpanded })} data-testid="map-legend">
      <div className="legend__header" onClick={() => setIsExpanded(prev => !prev)} role="button" tabIndex={-1}>
        <span className="legend__title">{formatMessage({ defaultMessage: 'Legend', id: 'legend.title' })}</span>
        <i className="fa-solid fa-chevron-left legend__chevron" />
      </div>
      <div className="legend__items">
        <div className="legend__itemsInner">
          {items.map(item => (
            <div className="legend__item" key={item.label}>
              {item.isSwatch ? (
                <svg
                  className={cn('legend__swatch', {
                    [`legend__swatch--${item.swatchModifier}`]: !!item.swatchModifier
                  })}
                  height="12"
                  viewBox="0 0 12 12"
                  width="12"
                >
                  <circle cx="6" cy="6" r="5.6" />
                </svg>
              ) : item.hasCircle ? (
                <span
                  className={cn('legend__iconCircle', {
                    [`legend__iconCircle--${item.circleModifier}`]: !!item.circleModifier
                  })}
                >
                  <i className={cn('legend__icon', item.icon)} />
                </span>
              ) : (
                <i className={cn('legend__icon', item.icon)} />
              )}
              <span className="legend__label">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
